/**
 * Service Initialization Module
 *
 * This module handles initialization of all places-related services
 * that need to be started when the application loads.
 */

import { placesSuggestionService } from './placesSuggestionService';
import { placesService } from './placesService';
import { backupRegistry } from '@trip-planner-monorepo/shared';
import { db } from '@trip-planner-monorepo/shared';
import { doc, setDoc } from 'firebase/firestore';

/**
 * Initialize all places services
 * This should be called when the application starts
 */
export const initPlacesServices = () => {
  console.log('Initializing places services...');

  // Register backup capabilities with the backup registry
  backupRegistry.register('places', {
    fetchAll: async (userId, tripId) => {
      console.log(`[Places] Fetching all places for backup: userId=${userId}, tripId=${tripId}`);
      return await placesService.fetchPlaces(userId, tripId);
    },
    restoreAll: async (userId, tripId, data) => {
      console.log(`[Places] Restoring ${data.length} places for trip ${tripId}`);

      // Data is an array of place documents from backup
      for (const placeData of data) {
        try {
          // Preserve original ID by using setDoc instead of addDoc
          const placeRef = doc(db, 'users', userId, 'trips', tripId, 'places', placeData.id);

          // Remove the id from the data object (it's already in the document reference)
          const { id, ...placeDataWithoutId } = placeData;

          // CRITICAL: Preserve timestamps from export instead of overwriting
          // Use setDoc to create/overwrite document with preserved ID
          await setDoc(placeRef, placeDataWithoutId);

          console.log(`[Places] Restored place ${placeData.id} (${placeData.name || 'no name'})`);

          // Log AI resources restoration for debugging
          if (placeDataWithoutId.aiResources) {
            const resourceCount = Object.values(placeDataWithoutId.aiResources)
              .reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0);
            console.log(`[Places]   └─ Restored ${resourceCount} AI resources`);
          }
          if (placeDataWithoutId.deletedAIResources) {
            console.log(`[Places]   └─ Restored ${placeDataWithoutId.deletedAIResources.length} deleted AI resources`);
          }
        } catch (error) {
          console.error(`[Places] Error restoring place ${placeData.id}:`, error);
          throw error;
        }
      }

      console.log(`[Places] Successfully restored ${data.length} places`);
    },
    version: '1.0'
  });

  console.log('[Places] Registered with backup registry');

  // Initialize the places service event subscriptions
  // This must be called after module initialization to avoid circular dependencies
  placesService.init();

  // TEMPORARILY DISABLED: Initialize the places suggestion service to listen for events
  // This is disabled to prevent conflicts with the new SuggestionService system
  // TODO: Remove this service entirely once new system is fully tested and working
  // placesSuggestionService.init();
  console.log('placesSuggestionService.init() DISABLED - using new SuggestionService instead');

  console.log('Places services initialized');
}; 